import { useEffect, useState } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { ArticleEntryData } from "@/types/articleEntryData.types";
import { DealLaScSection } from "@/types/dealLaSc.types";
import { useScroll } from "@FillInTableContainer/components/FillInTable/hooks/useScroll";
import { TableCreatorTextFieldContainer } from "@FillInTableContainer/shared";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import useCheckPressedShell from "@FillInTableContainer/shared/hooks/useCheckPressedShell";
import { useGetInfoOfShell } from "@FillInTableContainer/shared/hooks/useGetInfoOfShell";
import { getShellPlaceholder } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getShellTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import DealLaScTerritoryControlledModal from "./components/DealLaScTerritoryControlledModal/DealLaScTerritoryControlledModal";
import * as S from "./DealLaScRightsScopeShell.style";
import { useHandleDealLaScRightsScopeTableCreatorShell } from "./hooks/useHandleDealLaScRightsScopeTableCreatorShell";

export interface PositionInfoProps {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  subGroupShellIndex: number;
  subGroupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
}

interface Props extends PositionInfoProps {
  subGroupShellIndex: number;
  subGroupShellKey: string | null;
  isFocusedGroupShell: boolean;
  isClearGroupShell: boolean;
  isDeleteGroupShell: boolean;
  isAlertGroupShell: boolean;

  isClearSubGroupShell: boolean;
  isDeleteSubGroupShell: boolean;
  isFocusGroupShellContainer: boolean;
}

const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;

const DealLaScRightsScopeShell = ({
  groupTableKey,
  groupTableIndex,
  groupShellKey,
  groupShellIndex,
  tableKey,
  shellKey,
  tableIndex,
  shellIndex,
  subGroupShellIndex,
  subGroupShellKey,
  isClearGroupShell,
  isDeleteGroupShell,
  isAlertGroupShell,
  isClearSubGroupShell,
  isDeleteSubGroupShell,
  isFocusGroupShellContainer,
}: Props) => {
  const draftArticleId = getPressIdFromURL();
  const [entryData, setEntryData] = useEntryDataState(draftArticleId);
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const positionPropsInfo: PositionInfoProps = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
    shellKey,
    shellIndex,
  };

  const {
    isFocused,
    isClearShell,
    isEnableEdit,
    isAlertShell,
    focusStatus,
    columnSize,
    isClearTable,
    isDeleteTable,
    handleFocusOutShell,
    isBenefitingAxisDisabled,
  } = useHandleDealLaScRightsScopeTableCreatorShell(positionPropsInfo);
  const [isOpenTerritoryControlledModal, setIsOpenTerritoryControlledModal] =
    useState(false);
  const { isPressed } = useCheckPressedShell();

  const { shellValue } = useGetInfoOfShell(groupTableKey, articleSection);
  const { register } = useScroll();

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-dropdown="list-box"]'))
        return;
      if ((e.target as HTMLElement).closest("[data-button]")) return;
      if (isFocused) handleFocusOutShell();
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleFocusOutShell, isFocused]);

  const RIGHTS_SCOPE_SHELL_WIDTH = {
    Asset: "12rem",
    "Rights Scope Type": "12rem",
    "Rights Scope": "14.4rem",
    "Axis Direction": "2.4rem",
    "Benefiting Axis": "3.6rem",
    "Contributing Axis": "3.6rem",
    "Rights Limitation": "12.8rem",
    "Intended Use": "12rem",
    Exclusivity: "12rem",
    Territory: "12rem",
    "Territory Include": "12.8rem",
    "Territory Exclude": "12.8rem",
  };

  const RIGHTS_SCOPE_SHELL_MAX_WIDTH = {
    Asset: "12rem",
    "Rights Scope Type": "12rem",
    "Rights Scope": "14.4rem",
    "Axis Direction": "2.4rem",
    "Benefiting Axis": "3.6rem",
    "Contributing Axis": "3.6rem",
    "Rights Limitation": "12.8rem",
    "Intended Use": "12rem",
    Exclusivity: "12rem",
    Territory: "12rem",
    "Territory Include": "12.8rem",
    "Territory Exclude": "12.8rem",
  };

  const DISABLE_TEXT_EVENT_SHELL_KEYS = [
    "Axis Direction",
    "Benefiting Axis",
    "Contributing Axis",
    "Rights Scope",
  ];

  const ENTER_DATA_BY_CONTROLLED_TERRITORY_MODAL_SHELL_KEYS = [
    "Territory Include",
    "Territory Exclude",
  ];
  if (ENTER_DATA_BY_CONTROLLED_TERRITORY_MODAL_SHELL_KEYS.includes(shellKey)) {
    const shellValuePlaceholder = getShellTitle(articleSection, shellKey);
    const shellValueText = shellValue(
      shellKey,
      tableIndex,
      groupShellIndex,
      shellIndex,
      subGroupShellIndex,
    )
      .value.map(({ text }) => text)
      .join(", ");
    const isShellValue = !!shellValueText;
    return (
      <S.ShellWrapper
        column={columnSize}
        width={
          RIGHTS_SCOPE_SHELL_WIDTH?.[
            shellKey as keyof typeof RIGHTS_SCOPE_SHELL_WIDTH
          ] || "12.8rem"
        }
        maxWidth={
          RIGHTS_SCOPE_SHELL_MAX_WIDTH?.[
            shellKey as keyof typeof RIGHTS_SCOPE_SHELL_MAX_WIDTH
          ] || "auto"
        }
        {...register(
          `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
        )}
      >
        <S.ClickableShellBorderWrapper
          onClick={() => setIsOpenTerritoryControlledModal(true)}
          $isModified={shellValue(
            shellKey,
            tableIndex,
            groupShellIndex,
            shellIndex,
            subGroupShellIndex,
          ).value.some(({ is_modified }) => is_modified)}
          $isPlaceholder={!isShellValue}
        >
          {isShellValue ? shellValueText : shellValuePlaceholder}
        </S.ClickableShellBorderWrapper>
        {isOpenTerritoryControlledModal && (
          <DealLaScTerritoryControlledModal
            onClose={() => setIsOpenTerritoryControlledModal(false)}
            selectedInclude={shellValue(
              "Territory Include",
              tableIndex,
              groupShellIndex,
              shellIndex,
              subGroupShellIndex,
            ).value.flatMap(({ text }) => text.split(", "))}
            selectedExclude={shellValue(
              "Territory Exclude",
              tableIndex,
              groupShellIndex,
              shellIndex,
              subGroupShellIndex,
            ).value.flatMap(({ text }) => text.split(", "))}
            saveSelectedData={(
              selectedInclude: string[],
              selectedExclude: string[],
            ) => {
              const changedEntryData = {
                ...entryData,
                [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
                  ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
                  "Rights Scope Table": entryData?.[
                    ENTRY_DATA_SECTION_KEYS.dealsLaSc
                  ]?.["Rights Scope Table"]?.map(
                    (tableItem, tableItemIndex) => {
                      if (tableItemIndex === subGroupShellIndex) {
                        return {
                          ...tableItem,
                          "Territory Include": {
                            ...tableItem?.["Territory Include"],
                            value: selectedInclude.map((include) => ({
                              text: include,
                              is_modified: true,
                            })),
                          },
                          "Territory Exclude": {
                            ...tableItem?.["Territory Exclude"],
                            value: selectedExclude.map((exclude) => ({
                              text: exclude,
                              is_modified: true,
                            })),
                          },
                        };
                      }
                      return tableItem;
                    },
                  ),
                },
              } as ArticleEntryData;
              setEntryData(() => changedEntryData);

              changeCurrentHistory({
                entryData: changedEntryData,
                changedDataInfo: {
                  target: "value",
                  action: "update",
                  position: {
                    groupTableKey,
                    groupTableIndex,
                    tableKey,
                    tableIndex,
                    groupShellKey,
                    groupShellIndex,
                    shellKey,
                    shellIndex,
                    value: null,
                    valueIndex: null,
                  },
                },
              });
            }}
          />
        )}
      </S.ShellWrapper>
    );
  }
  if (
    shellKey === "Axis Direction" ||
    (shellKey === "Benefiting Axis" && isBenefitingAxisDisabled)
  ) {
    return (
      <S.ShellWrapper
        column={columnSize}
        width={
          RIGHTS_SCOPE_SHELL_WIDTH?.[
            shellKey as keyof typeof RIGHTS_SCOPE_SHELL_WIDTH
          ] || "12.8rem"
        }
        maxWidth={
          RIGHTS_SCOPE_SHELL_MAX_WIDTH?.[
            shellKey as keyof typeof RIGHTS_SCOPE_SHELL_MAX_WIDTH
          ] || "auto"
        }
        {...register(
          `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
        )}
      >
        <S.DisabledShellBorderWrapper>
          {shellValue(
            shellKey,
            tableIndex,
            groupShellIndex,
            shellIndex,
            subGroupShellIndex,
          )
            .value.map(({ text }) => text.replace("RIGHT_ARROW", "→"))
            .join(", ")}
        </S.DisabledShellBorderWrapper>
      </S.ShellWrapper>
    );
  }

  return (
    <S.ShellWrapper
      column={columnSize}
      width={
        RIGHTS_SCOPE_SHELL_WIDTH?.[
          shellKey as keyof typeof RIGHTS_SCOPE_SHELL_WIDTH
        ] || "12.8rem"
      }
      maxWidth={
        RIGHTS_SCOPE_SHELL_MAX_WIDTH?.[
          shellKey as keyof typeof RIGHTS_SCOPE_SHELL_MAX_WIDTH
        ] || "auto"
      }
      {...register(
        `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
      )}
    >
      <S.ShellBorderWrapper
        isFocused={isFocused && focusStatus.type !== "dragging"}
        isClearStatus={
          isClearShell ||
          isClearGroupShell ||
          isClearSubGroupShell ||
          isClearTable ||
          isFocusGroupShellContainer
        }
        isDeleteStatus={
          isDeleteGroupShell || isDeleteSubGroupShell || isDeleteTable
        }
        isAlertStatus={isAlertShell || isAlertGroupShell}
        isDisableEdit={!isEnableEdit}
        $isDragging={isFocused && focusStatus.type === "dragging"}
        $isPressed={isPressed}
        onClick={(e) => {
          e.stopPropagation();
          if (DISABLE_TEXT_EVENT_SHELL_KEYS.includes(shellKey)) return;
          const contenteditable = (
            e.currentTarget as HTMLElement
          ).querySelector('[contenteditable="true"]');
          if (!(contenteditable instanceof HTMLElement)) return;
          contenteditable.focus();
          const sel = window.getSelection();
          if (sel && sel.toString().length > 0) return;
          const textNode = contenteditable.firstChild || contenteditable;
          const textLength = textNode.textContent?.length || 0;
          const range = document.createRange();
          range.setStart(textNode, textLength);
          range.setEnd(textNode, textLength);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }}
        onMouseDown={(e) => {
          if (DISABLE_TEXT_EVENT_SHELL_KEYS.includes(shellKey)) return;
          const contenteditable = (
            e.currentTarget as HTMLElement
          ).querySelector('[contenteditable="true"]');
          if (contenteditable instanceof HTMLElement) contenteditable.focus();
        }}
        $isTextEvent={!DISABLE_TEXT_EVENT_SHELL_KEYS.includes(shellKey)}
      >
        <TableCreatorTextFieldContainer
          placeholder={
            getShellPlaceholder(articleSection, shellKey) ||
            getShellTitle(articleSection, shellKey)
          }
          shellValue={shellValue(
            shellKey,
            tableIndex,
            groupShellIndex,
            shellIndex,
            subGroupShellIndex,
          )}
          shellKey={shellKey}
          shellIndex={shellIndex}
          groupTableKey={groupTableKey}
          groupTableIndex={groupTableIndex}
          groupShellKey={groupShellKey}
          groupShellIndex={groupShellIndex}
          subGroupShellKey={subGroupShellKey}
          subGroupShellIndex={subGroupShellIndex}
          tableKey={tableKey}
          tableIndex={tableIndex}
          articleSectionKey={articleSection}
        />
      </S.ShellBorderWrapper>
    </S.ShellWrapper>
  );
};

export default DealLaScRightsScopeShell;
